import React, { useState, useMemo, useEffect } from 'react';
import { format, getYear, getMonth } from 'date-fns';
import { formatCurrency } from '../utils/formatCurrency';

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const TransactionsList = ({ transactions, onDeleteTransaction }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [forceRefresh, setForceRefresh] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 75;
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());

  // Forzar actualización cuando cambian las transacciones
  useEffect(() => {
    setForceRefresh(prev => prev + 1);
    // Limpiar selecciones si las transacciones cambian
    setSelectedTransactions(new Set());
  }, [transactions]);

  // Obtener años disponibles - mejorado con validación
  const availableYears = useMemo(() => {
    if (!transactions || transactions.length === 0) return [new Date().getFullYear()];
    
    const years = transactions
      .filter(t => t && t.date) // Filtrar transacciones válidas
      .map(t => {
        try {
          return getYear(new Date(t.date));
        } catch (e) {
          console.warn('Data inválida encontrada:', t.date);
          return null;
        }
      })
      .filter(year => year !== null);
    
    return [...new Set(years)].sort((a, b) => b - a);
  }, [transactions, forceRefresh]);

  // Obtener meses disponibles para el año seleccionado - mejorado
  const availableMonths = useMemo(() => {
    if (!transactions || transactions.length === 0) return [new Date().getMonth()];
    
    const yearTransactions = transactions
      .filter(t => t && t.date)
      .filter(t => {
        try {
          return getYear(new Date(t.date)) === selectedYear;
        } catch (e) {
          return false;
        }
      });
    
    console.log('Year transactions dates:', yearTransactions.map(t => t.date));
    
    const months = yearTransactions
      .map(t => {
        try {
          return getMonth(new Date(t.date));
        } catch (e) {
          return null;
        }
      })
      .filter(month => month !== null);
    
    console.log('All months from transactions:', months);
    const uniqueMonths = [...new Set(months)].sort((a, b) => a - b);
    console.log('Available months calculated:', uniqueMonths);
    console.log('Available months names:', uniqueMonths.map(m => MONTH_NAMES[m]));
    return uniqueMonths;
  }, [transactions, selectedYear, forceRefresh]);

  // Obtener categorías disponibles con conteo - mejorado
  const availableCategories = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    
    const yearMonthTransactions = transactions
      .filter(t => t && t.date && t.expenseType)
      .filter(t => {
        try {
          const date = new Date(t.date);
          return getYear(date) === selectedYear && getMonth(date) === selectedMonth;
        } catch (e) {
          return false;
        }
      });
    
    const categoryCounts = {};
    yearMonthTransactions.forEach(t => {
      const category = t.expenseType;
      if (category) {
        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      }
    });
    
    // Orden específico de categorías - agregando OTHERS
    const categoryOrder = ['FOOD', 'GO OUT', 'BILLS', 'AMANDA', 'MARTIN', 'HOLIDAYS', 'HOUSE', 'OTHERS'];
    
    return Object.keys(categoryCounts)
      .sort((a, b) => {
        const indexA = categoryOrder.indexOf(a);
        const indexB = categoryOrder.indexOf(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      })
      .map(category => ({
        name: category,
        count: categoryCounts[category],
        total: yearMonthTransactions
          .filter(t => t.expenseType === category)
          .reduce((sum, t) => sum + (t.gbpAmount || 0), 0)
      }));
  }, [transactions, selectedYear, selectedMonth, forceRefresh]);

  // Filtrar transacciones - mejorado con mejor manejo de errores
  const filteredTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      console.log('Nenhuma transação disponível');
      return [];
    }
    
    console.log('Filtering transactions:', {
      totalCount: transactions.length,
      selectedYear,
      selectedMonth,
      searchTerm,
      selectedCategoriesSize: selectedCategories.size
    });
    
    const filtered = transactions
      .filter(t => {
        // Verificar que la transacción tenga datos válidos
        if (!t || !t.date || !t.merchantName) {
          console.log('Transação inválida filtrada:', t);
          return false;
        }
        
        try {
          const date = new Date(t.date);
          const year = getYear(date);
          const month = getMonth(date);
          
          // Filtro por fecha
          const dateMatch = year === selectedYear && month === selectedMonth;
          if (!dateMatch) {
            return false;
          }
          
          // Filtro por categorías (si hay categorías seleccionadas)
          const categoryMatch = selectedCategories.size === 0 || 
            (t.expenseType && selectedCategories.has(t.expenseType));
          if (!categoryMatch) {
            return false;
          }
          
          // Filtro por búsqueda - mejorado
          let searchMatch = true;
          if (searchTerm.trim() !== '') {
            const searchLower = searchTerm.toLowerCase();
            const searchableFields = [
              t.merchantName || '',
              t.expenseType || '',
              t.moneySource || '',
              t.currency || '',
              t.bankName || '',
              (t.originalAmount || 0).toString(),
              (t.gbpAmount || 0).toString()
            ];
            
            searchMatch = searchableFields.some(field => 
              field.toLowerCase().includes(searchLower)
            );
          }
          
          return searchMatch;
        } catch (e) {
          console.warn('Erro processando transação:', t, e);
          return false;
        }
      })
      .sort((a, b) => {
        try {
          return new Date(b.date) - new Date(a.date);
        } catch (e) {
          return 0;
        }
      });
    
    console.log('Filtered result:', {
      filteredCount: filtered.length,
      sampleFiltered: filtered[0]
    });
    
    return filtered;
  }, [transactions, selectedYear, selectedMonth, selectedCategories, searchTerm, forceRefresh]);

  // Calcular paginación
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Reiniciar página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedMonth, selectedCategories, searchTerm]);

  // Asegurar que el año y mes estén disponibles
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      console.log('Ano selecionado não disponível, configurando para primeiro ano disponível');
      setSelectedYear(availableYears[0]);
    }
    
    if (availableMonths.length > 0 && !availableMonths.includes(selectedMonth)) {
      console.log('Mês selecionado não disponível, configurando para primeiro mês disponível');
      setSelectedMonth(availableMonths[0]);
    }
  }, [availableYears, availableMonths, selectedYear, selectedMonth]);

  // Debug: Log para verificar transacciones
  useEffect(() => {
    console.log('TransactionsList Debug:', {
      totalTransactions: transactions?.length || 0,
      filteredTransactions: filteredTransactions?.length || 0,
      paginatedTransactions: paginatedTransactions?.length || 0,
      selectedYear,
      selectedMonth,
      availableYears,
      availableMonths,
      searchTerm,
      selectedCategories: Array.from(selectedCategories),
      sampleTransaction: transactions?.[0],
      currentPage,
      totalPages
    });
  }, [transactions, filteredTransactions, paginatedTransactions, selectedYear, selectedMonth, searchTerm, selectedCategories, availableYears, availableMonths, currentPage, totalPages]);

  const handleDeleteTransaction = (transactionId) => {
    if (!transactions || transactions.length === 0) return;
    
    const transaction = transactions.find(t => t && t.id === transactionId);
    if (transaction) {
      const confirmed = window.confirm(
        `Tem certeza de que deseja excluir esta transação?\n\n` +
        `Data: ${format(new Date(transaction.date), 'dd/MM/yyyy')}\n` +
        `Estabelecimento: ${transaction.merchantName}\n` +
        `Valor: ${formatCurrency(transaction.gbpAmount || 0)}\n` +
        `Categoria: ${transaction.expenseType || 'N/A'}`
      );
      
      if (confirmed) {
        onDeleteTransaction(transactionId);
        setForceRefresh(prev => prev + 1);
      }
    }
  };

  // Funciones para manejar categorías
  const handleCategoryToggle = (category) => {
    const newSelectedCategories = new Set(selectedCategories);
    if (newSelectedCategories.has(category)) {
      newSelectedCategories.delete(category);
    } else {
      newSelectedCategories.add(category);
    }
    setSelectedCategories(newSelectedCategories);
  };

  const handleSelectAllCategories = () => {
    setSelectedCategories(new Set());
  };

  const handleSelectOnlyCategory = (category) => {
    setSelectedCategories(new Set([category]));
  };

  // Funciones para paginación
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleGoToPage = (page) => {
    setCurrentPage(page);
  };

  // Funciones para selección múltiple
  const handleSelectTransaction = (transactionId) => {
    console.log('Toggling selection for transaction:', transactionId);
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
      console.log('Deselected transaction:', transactionId);
    } else {
      newSelected.add(transactionId);
      console.log('Selected transaction:', transactionId);
    }
    setSelectedTransactions(newSelected);
    console.log('New selection state:', Array.from(newSelected));
  };

  const handleSelectAll = () => {
    console.log('HandleSelectAll called');
    console.log('Current selectedTransactions.size:', selectedTransactions.size);
    console.log('paginatedTransactions.length:', paginatedTransactions.length);
    
    const currentPageIds = paginatedTransactions.map(t => t.id);
    console.log('Current page transaction IDs:', currentPageIds);
    
    // Verificar si todas las transacciones de la página actual están seleccionadas
    const allCurrentPageSelected = currentPageIds.every(id => selectedTransactions.has(id));
    console.log('All current page selected:', allCurrentPageSelected);
    
    if (allCurrentPageSelected && currentPageIds.length > 0) {
      // Si todas están seleccionadas, deseleccionar todas de esta página
      const newSelected = new Set(selectedTransactions);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedTransactions(newSelected);
      console.log('Deselected all from current page');
    } else {
      // Seleccionar todas las de la página actual (mantener las de otras páginas)
      const newSelected = new Set(selectedTransactions);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedTransactions(newSelected);
      console.log('Selected all from current page');
    }
  };

  const handleBulkDelete = () => {
    if (selectedTransactions.size === 0) {
      alert('Não há transações selecionadas para excluir.');
      return;
    }

    const selectedCount = selectedTransactions.size;
    const selectedIds = Array.from(selectedTransactions);
    
    console.log('Iniciando borrado masivo:', {
      selectedCount,
      selectedIds,
      selectedTransactions: Array.from(selectedTransactions)
    });
    
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres eliminar ${selectedCount} transacciones seleccionadas?\n\n` +
      `IDs a eliminar: ${selectedIds.join(', ')}\n\n` +
      `Esta acción no se puede deshacer.`
    );
    
    if (confirmed) {
      console.log('Confirmado - Eliminando transacciones con IDs:', selectedIds);
      
      // Eliminar todas las transacciones de una vez usando un enfoque más simple
      let successCount = 0;
      
      // Crear un array de transacciones a eliminar para debug
      const transactionsToDelete = selectedIds.map(id => 
        transactions.find(t => t.id === id)
      ).filter(Boolean);
      
      console.log('Transacciones a eliminar:', transactionsToDelete);
      
      // Eliminar una por una pero esperando que se complete el estado
      for (const transactionId of selectedIds) {
        try {
          console.log(`Eliminando transacción ID: ${transactionId}`);
          onDeleteTransaction(transactionId);
          successCount++;
        } catch (error) {
          console.error(`Error eliminando transacción ${transactionId}:`, error);
        }
      }
      
      console.log(`Eliminación completada: ${successCount}/${selectedCount} transacciones`);
      
      // Limpiar selecciones
      setSelectedTransactions(new Set());
      setForceRefresh(prev => prev + 1);
      
      if (successCount === selectedCount) {
        alert(`${successCount} transacciones eliminadas exitosamente`);
      } else {
        alert(`${successCount} de ${selectedCount} transacciones eliminadas`);
      }
    }
  };

  // Función para detectar duplicados
  const detectDuplicates = () => {
    if (!transactions || transactions.length === 0) return [];
    
    const duplicates = [];
    const seenTransactions = new Map();
    
    transactions.forEach(transaction => {
      if (!transaction) return;
      
      // Crear una clave única basada en múltiples criterios
      const key = `${transaction.merchantName}-${transaction.date}-${transaction.originalAmount}-${transaction.currency}`;
      
      if (seenTransactions.has(key)) {
        // Es un duplicado
        const original = seenTransactions.get(key);
        duplicates.push({
          original,
          duplicate: transaction,
          key,
          criteria: {
            merchantName: transaction.merchantName,
            date: transaction.date,
            originalAmount: transaction.originalAmount,
            currency: transaction.currency
          }
        });
      } else {
        seenTransactions.set(key, transaction);
      }
    });
    
    return duplicates;
  };

  const handleRemoveDuplicates = () => {
    const duplicates = detectDuplicates();
    
    if (duplicates.length === 0) {
      alert('Não foram encontradas transações duplicadas.');
      return;
    }
    
    const duplicateIds = duplicates.map(d => d.duplicate.id);
    
    const confirmed = window.confirm(
      `Foram encontradas ${duplicates.length} transações duplicadas.\n\n` +
      `Deseja excluir as duplicadas automaticamente?\n\n` +
      `Duplicados encontrados:\n${duplicates.map(d => `- ${d.criteria.merchantName} (${d.criteria.date})`).join('\n')}`
    );
    
    if (confirmed) {
      duplicateIds.forEach(id => {
        onDeleteTransaction(id);
      });
      
      setForceRefresh(prev => prev + 1);
      alert(`${duplicates.length} transações duplicadas excluídas`);
    }
  };

  return (
    <div className="expense-card">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
            Lista de Transações
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Gerencie e exclua suas transações
          </p>
        </div>
        <button 
          type="button"
          className="button-secondary text-xl"
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-4 space-y-4">
          
          {/* Debug Information - Temporary */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold text-yellow-900">Información de Debug (temporal):</h4>
              <div className="flex space-x-2">
                <button
                  onClick={handleRemoveDuplicates}
                  className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  Eliminar Duplicados
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('¿Estás seguro de que quieres eliminar TODAS las transacciones?\n\nEsta acción no se puede deshacer.')) {
                      transactions.forEach(t => onDeleteTransaction(t.id));
                      setForceRefresh(prev => prev + 1);
                      alert('Todas las transacciones han sido eliminadas');
                    }
                  }}
                  className="px-2 py-1 text-xs bg-red-200 text-red-800 rounded hover:bg-red-300 transition-colors"
                >
                  Eliminar Todas
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-yellow-800">
              <div>Total transacciones: <strong>{transactions?.length || 0}</strong></div>
              <div>Filtradas: <strong>{filteredTransactions?.length || 0}</strong></div>
              <div>En página actual: <strong>{paginatedTransactions?.length || 0}</strong></div>
              <div>Seleccionadas: <strong>{selectedTransactions.size}</strong></div>
              <div>Año seleccionado: <strong>{selectedYear}</strong></div>
              <div>Mes seleccionado: <strong>{MONTH_NAMES[selectedMonth]}</strong></div>
              <div>Años disponibles: <strong>{availableYears.join(', ')}</strong></div>
              <div>Meses disponibles: <strong>{availableMonths.map(m => MONTH_NAMES[m]).join(', ')}</strong></div>
            </div>
            {transactions && transactions.length > 0 && (
              <div className="mt-2 text-yellow-700">
                <strong>Ejemplo de transacción:</strong> {transactions[0]?.merchantName} - {transactions[0]?.bankName || 'Sin banco'} - {transactions[0]?.date}
              </div>
            )}
            {(() => {
              const duplicates = detectDuplicates();
              return duplicates.length > 0 && (
                <div className="mt-2 text-red-700">
                  <strong>Duplicados detectados:</strong> {duplicates.length} transacciones duplicadas encontradas
                </div>
              );
            })()}
          </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ano
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="select-field"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mês
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="select-field"
              >
                {availableMonths.map(month => (
                  <option key={month} value={month}>{MONTH_NAMES[month]}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field"
                placeholder="Buscar por estabelecimento, banco, categoria..."
              />
            </div>
          </div>

          {/* Filtros por Categorías */}
          <div className="expense-card-dark">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Filtrar por Categorías
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={handleSelectAllCategories}
                  className="button-secondary text-sm"
                >
                  Todas
                </button>
                <span className="text-sm text-gray-300 bg-black bg-opacity-20 px-3 py-1 rounded-full">
                  {selectedCategories.size === 0 ? 'Sin filtro' : `${selectedCategories.size} seleccionadas`}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {availableCategories.map(categoryData => (
                <div key={categoryData.name} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-2 hover:bg-gray-50 transition-colors">
                  <label className="flex items-center cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={selectedCategories.has(categoryData.name)}
                      onChange={() => handleCategoryToggle(categoryData.name)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getCategoryColor(categoryData.name)}`}>
                          {categoryData.name}
                        </span>
                        <div className="text-xs text-gray-500 ml-2">
                          {categoryData.count} transacciones
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        Total: {formatCurrency(categoryData.total)}
                      </div>
                    </div>
                  </label>
                  <button
                    onClick={() => handleSelectOnlyCategory(categoryData.name)}
                    className="ml-2 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                    title="Solo esta categoría"
                  >
                    Só esta
                  </button>
                </div>
              ))}
            </div>
            
            {availableCategories.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No hay categorías disponibles para el mes seleccionado
              </p>
            )}
          </div>

          {/* Resumen */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">
                Resumen de {MONTH_NAMES[selectedMonth]} {selectedYear}
                {selectedCategories.size > 0 && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    (Filtrado: {selectedCategories.size} categorías)
                  </span>
                )}
              </h3>
              
              {/* Botón de eliminación en masa */}
              {selectedTransactions.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="button-error flex items-center space-x-2"
                >
                  <span>Eliminar Seleccionadas ({selectedTransactions.size})</span>
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {filteredTransactions.length}
                </div>
                <div className="text-sm text-gray-600">Transacciones</div>
                {totalPages > 1 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Página {currentPage} de {totalPages}
                  </div>
                )}
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(filteredTransactions.reduce((sum, t) => sum + (t.gbpAmount || 0), 0))}
                </div>
                <div className="text-sm text-gray-600">Total GBP</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {[...new Set(filteredTransactions.map(t => t.expenseType))].length}
                </div>
                <div className="text-sm text-gray-600">Categorías Visibles</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {filteredTransactions.length > 0 ? 
                    formatCurrency(filteredTransactions.reduce((sum, t) => sum + (t.gbpAmount || 0), 0) / filteredTransactions.length) : 
                    '£0.00'
                  }
                </div>
                <div className="text-sm text-gray-600">Promedio</div>
              </div>
            </div>
            
            {/* Mostrar categorías seleccionadas */}
            {selectedCategories.size > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-sm text-gray-600 mb-2">Categorías seleccionadas:</div>
                <div className="flex flex-wrap gap-1">
                  {Array.from(selectedCategories).map(category => (
                    <span key={category} className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(category)}`}>
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Lista de transacciones */}
          <div className="space-y-3">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No hay transacciones para {MONTH_NAMES[selectedMonth]} {selectedYear}</p>
                {selectedCategories.size > 0 && (
                  <p className="mt-2 text-sm">
                    con las categorías seleccionadas: {Array.from(selectedCategories).join(', ')}
                  </p>
                )}
                {searchTerm && (
                  <p className="mt-2 text-sm">
                    y que coincidan con: "{searchTerm}"
                  </p>
                )}
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Consejo:</strong> Verifica que hayas seleccionado el mes correcto{selectedCategories.size > 0 ? ' y las categorías adecuadas' : ''}.
                    Si agregaste transacciones con fechas de otros meses, selecciona el mes correspondiente.
                  </p>
                  <p className="text-xs text-yellow-700 mt-2">
                    Ejemplo: Si agregaste transacciones de Mayo 2025, selecciona "Mayo" en el filtro.
                  </p>
                  {selectedCategories.size > 0 && (
                    <p className="text-xs text-yellow-700 mt-1">
                      También puedes hacer clic en "Todas" para ver todas las categorías.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={paginatedTransactions.length > 0 && paginatedTransactions.every(t => selectedTransactions.has(t.id))}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          title="Seleccionar todas en esta página"
                        />
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Fecha
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Comercio
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Monto Original
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Monto GBP
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Categoría
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Fuente
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Banco
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-700">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-4 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.has(transaction.id)}
                            onChange={() => handleSelectTransaction(transaction.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm">
                          {format(new Date(transaction.date), 'dd/MM/yyyy')}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm">
                          <div className="font-medium">{transaction.merchantName}</div>
                          {transaction.isSplit && (
                            <div className="text-xs text-gray-500">Split</div>
                          )}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm">
                          {(transaction.originalAmount || 0).toFixed(2)} {transaction.currency || 'GBP'}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm font-medium">
                          {formatCurrency(transaction.gbpAmount || 0)}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(transaction.expenseType)}`}>
                            {transaction.expenseType || 'N/A'}
                          </span>
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(transaction.moneySource)}`}>
                            {transaction.moneySource || 'N/A'}
                          </span>
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-sm">
                          {transaction.bankName && transaction.bankName.trim() !== '' ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {transaction.bankName}
                            </span>
                          ) : (
                            <div className="text-center">
                              <span className="text-gray-400 text-xs italic">Sin banco</span>
                              <div className="text-xs text-gray-300 mt-1">
                                ID: {transaction.id}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-center">
                          <button
                            onClick={() => handleDeleteTransaction(transaction.id)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                            title="Eliminar transacción"
                          >
                            X
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              
                {/* Controles de paginación */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded text-sm ${
                          currentPage === 1 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        ← Anterior
                      </button>
                      
                      <span className="text-sm text-gray-600">
                        Mostrando {startIndex + 1} - {Math.min(endIndex, filteredTransactions.length)} de {filteredTransactions.length}
                      </span>
                      
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded text-sm ${
                          currentPage === totalPages
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        Siguiente →
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => handleGoToPage(page)}
                          className={`px-2 py-1 rounded text-sm ${
                            currentPage === page
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Información adicional */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-medium text-yellow-900 mb-2">Información Importante:</h3>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Al eliminar una transacción, también se eliminará del archivo Excel correspondiente</li>
              <li>• Esta acción no se puede deshacer</li>
              <li>• Los archivos Excel se actualizarán automáticamente</li>
              <li>• Los filtros por categoría te permiten enfocarte en gastos específicos</li>
              <li>• Usa el botón "Só esta" para ver solo una categoría, o marca varias categorías para compararlas</li>
            </ul>
          </div>

        </div>
      )}
    </div>
  );
};

// Función para obtener color de categoría
const getCategoryColor = (category) => {
  const colors = {
    'FOOD': 'bg-green-100 text-green-800',
    'GO OUT': 'bg-orange-100 text-orange-800',
    'BILLS': 'bg-red-100 text-red-800',
    'AMANDA': 'bg-pink-100 text-pink-800',
    'MARTIN': 'bg-blue-100 text-blue-800',
    'HOLIDAYS': 'bg-purple-100 text-purple-800',
    'HOUSE': 'bg-gray-100 text-gray-800',
    'OTHERS': 'bg-gray-100 text-gray-800' // Added OTHERS color
  };
  return colors[category] || 'bg-gray-100 text-gray-800';
};

// Función para obtener color de fuente
const getSourceColor = (source) => {
  const colors = {
    'MARTIN BANK': 'bg-blue-100 text-blue-800',
    'AMANDA BANK': 'bg-pink-100 text-pink-800'
  };
  return colors[source] || 'bg-gray-100 text-gray-800';
};

export default TransactionsList; 